import axios from 'axios';

/**
 * Keep the session alive across access-token expiry and server restarts (ROO-124),
 * and recover from a truly-dead session (ROO-117).
 *
 * Access tokens last only ~15 min. Without this, any 401 (a normal expiry, or a
 * transient 401 right after a server redeploy) clears the session and bounces the
 * user to /login. Here we instead try the 7-day refresh token first:
 *   on 401 → POST /api/auth/refresh (single-flight) → store the new access token →
 *   retry the original request once. Only if the refresh ITSELF fails do we clear
 *   the session and redirect to /login.
 *
 * Only affects calls made via the default `axios` (the student dashboard /
 * placement bare-axios calls). Instances from axios.create() keep their own chains.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Access token lives under different keys depending on the login path
// (Login/Signup → accessToken, AuthContext → cleveroo_token); refresh token is
// always `refreshToken`.
function setAccessToken(token: string): void {
  let wrote = false;
  if (localStorage.getItem('accessToken') !== null) { localStorage.setItem('accessToken', token); wrote = true; }
  if (localStorage.getItem('cleveroo_token') !== null) { localStorage.setItem('cleveroo_token', token); wrote = true; }
  if (!wrote) localStorage.setItem('accessToken', token);
}
function clearSession(): void {
  ['cleveroo_token', 'accessToken', 'cleveroo_user', 'refreshToken'].forEach((k) => localStorage.removeItem(k));
}

// Single-flight: concurrent 401s share one refresh round-trip.
let refreshPromise: Promise<string | null> | null = null;
function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;
    try {
      const resp = await axios.post(
        `${API_BASE}/api/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const token = resp?.data?.data?.accessToken || resp?.data?.accessToken;
      if (token) { setAccessToken(token); return token; }
      return null;
    } catch {
      return null;
    }
  })().finally(() => { refreshPromise = null; });
  return refreshPromise;
}

let redirecting = false;

export function installAxiosAuthRecovery(): void {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config || {};
      const url: string = original.url || '';
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const onAuthPage = /^\/(login|signup)/.test(path);
      // Never try to refresh the auth calls themselves (avoids recursion).
      const isAuthCall = /\/api\/auth\/(login|refresh|register|google)/.test(url);

      if (status !== 401 || (original as any)._retry || isAuthCall || onAuthPage) {
        return Promise.reject(error);
      }
      (original as any)._retry = true;

      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return axios(original); // replay the original request once with the fresh token
      }

      // Refresh failed (no/expired refresh token) → the session is really over.
      if (!redirecting) {
        redirecting = true;
        clearSession();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    },
  );
}
